import { cn } from 'cn';
import { ChevronDownIcon } from 'lucide-react';

import type { ServiceOffering } from '@/lib/services';

/**
 * The services in a category as rows that open to their longer explanation. Native `<details>`, so
 * the explanation is in the pre-rendered page and opens without any script.
 */
export function ServiceOfferings({
  offerings,
  firstOpen = false,
  dark = false,
}: {
  offerings: ServiceOffering[];
  /** Start with the first row open; only the page's first list does. */
  firstOpen?: boolean;
  dark?: boolean;
}) {
  return (
    <div className={cn('border-b', dark ? 'border-[#2c3f5c]' : 'border-line')}>
      {offerings.map((offering, index) => (
        <details
          key={offering.title()}
          open={firstOpen && index === 0}
          // One row open across the page, so the photo beside a list cannot stretch with it. `name` is newer than
          // the site's browser floor (Safari 17.2); older browsers ignore it and let several rows open, which is harmless.
          name='service-offerings'
          className={cn('group border-t', dark ? 'border-[#2c3f5c]' : 'border-line')}
        >
          <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-5.5 [&::-webkit-details-marker]:hidden'>
            <span>
              <span className='mb-1 block text-[19px] font-bold'>{offering.title()}</span>
              <span className={cn('block leading-relaxed', dark ? 'text-[#c8d3e2]' : 'text-body')}>
                {offering.description()}
              </span>
            </span>
            {offering.detail && (
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-full transition-transform group-open:rotate-180',
                  dark ? 'bg-navy-soft text-white' : 'bg-mist',
                )}
              >
                <ChevronDownIcon className='size-4.5' />
              </span>
            )}
          </summary>
          {offering.detail && (
            <p className={cn('mb-6 max-w-160 leading-[1.7]', dark ? 'text-[#c8d3e2]' : 'text-body')}>
              {offering.detail()}
            </p>
          )}
        </details>
      ))}
    </div>
  );
}
